import { ContentWithBottomDrawer } from '@monorepo/shared/componentsV2/BottomDrawer';
import { Page } from '@monorepo/shared/componentsV2/fullPageWizard/styled/fullPageWizard';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { useToast } from '@monorepo/shared/contexts';
import { useDisableSubnav } from '@monorepo/shared/hooks/subnav/useDisableSubnav';
import { useWorkflow } from '@monorepo/shared/hooks/workflows/useWorkflow';
import { EditWorkflowHeader } from '@monorepo/workflowDataSetup/src/components/EditWorkflowHeader';
import { EmptyStepList } from '@monorepo/workflowDataSetup/src/components/EditWorkflowStepsPage/EmptyStepList';
import { WizardStep } from '@monorepo/workflowDataSetup/src/components/types';
import {
  SaveWorkflowStepRequest,
  WorkflowStepOperationType,
  WorkflowStepResponse,
} from 'mapistry-shared';
import React, { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useWorkflowStepCreate } from '../../hooks/useWorkflowStepCreate';
import { useWorkflowStepUpdate } from '../../hooks/useWorkflowStepUpdate';
import { WorkflowIncompleteDataMessages } from '../shared/WorkflowIncompleteDataMessages';
import { EditStepModal } from './EditStepModal';
import { StepList } from './StepList';
import { WorkflowStepPreviewBottomDrawer } from './WorkflowStepPreviewBottomDrawer';

const StyledWorkflowIncompleteDataMessages = styled(
  WorkflowIncompleteDataMessages,
)`
  margin-bottom: 1.25rem;
`;

function useOpenCloseEditStepModal() {
  const [currentlyOpenStep, setCurrentlyOpenStep] = useState<
    | {
        id: string | undefined;
        type: WorkflowStepOperationType;
      }
    | undefined
  >(undefined);

  const openModalForNewStep = useCallback(
    (stepType: WorkflowStepOperationType) => {
      setCurrentlyOpenStep({
        id: undefined,
        type: stepType,
      });
    },
    [],
  );

  const openModalForExistingStep = useCallback(
    (step: Pick<WorkflowStepResponse, 'id' | 'type'>) => {
      setCurrentlyOpenStep(step);
    },
    [],
  );

  const closeStepModal = useCallback(() => {
    setCurrentlyOpenStep(undefined);
  }, []);

  return {
    currentlyOpenStep,
    openModalForNewStep,
    openModalForExistingStep,
    closeStepModal,
  };
}

function useOpenClosePreviewStepWindow() {
  const [currentPreviewStep, setCurrentPreviewStep] = useState<
    WorkflowStepResponse | undefined
  >(undefined);

  const openPreviewStepPane = useCallback((step: WorkflowStepResponse) => {
    setCurrentPreviewStep(step);
  }, []);

  const closePreviewStepPane = useCallback(() => {
    setCurrentPreviewStep(undefined);
  }, []);

  return {
    currentPreviewStep,
    openPreviewStepPane,
    closePreviewStepPane,
  };
}

export function EditWorkflowStepsPage() {
  useDisableSubnav();
  const { organizationId, workflowId } = useParams();
  const stepListContainerRef = useRef<HTMLDivElement>(null);
  const stepsListAddButtonRef = useRef<HTMLButtonElement>(null);

  if (!organizationId || !workflowId) {
    throw new Error(
      "EditWorkflowStepsPage page was rendered with url that doesn't have organizationId or workflowId",
    );
  }

  const {
    closeStepModal,
    currentlyOpenStep,
    openModalForExistingStep,
    openModalForNewStep,
  } = useOpenCloseEditStepModal();

  const { id: currentlyOpenStepId, type: currentlyOpenStepType } =
    currentlyOpenStep ?? {};

  const { currentPreviewStep, closePreviewStepPane, openPreviewStepPane } =
    useOpenClosePreviewStepWindow();

  const { showUserFriendlyErrorToast, success: showSuccessToast } = useToast();

  const [creator] = useWorkflowStepCreate({ config: { throwOnError: true } });
  const [updater] = useWorkflowStepUpdate({ config: { throwOnError: true } });

  const submitStep = useCallback(
    async (step: SaveWorkflowStepRequest) => {
      const saveProps = { organizationId, workflowId, step };
      try {
        currentlyOpenStepId
          ? await updater({ ...saveProps, stepId: currentlyOpenStepId })
          : await creator(saveProps);

        showSuccessToast(
          `Workflow step has been ${
            currentlyOpenStepId ? 'edited.' : 'created.'
          }`,
        );
        closeStepModal();
      } catch (err) {
        showUserFriendlyErrorToast(
          err,
          `Cannot ${currentlyOpenStepId ? 'update' : 'save'} Workflow step.`,
          { dontAutoHide: true },
        );
      }
    },
    [
      closeStepModal,
      creator,
      currentlyOpenStepId,
      organizationId,
      showSuccessToast,
      showUserFriendlyErrorToast,
      updater,
      workflowId,
    ],
  );

  const { workflow, isLoading } = useWorkflow({ organizationId, workflowId });

  const onDeleteSuccess = useCallback(
    (deletedStepId: string) => {
      if (deletedStepId === currentPreviewStep?.id) {
        closePreviewStepPane();
      }

      // when there is only one step left, put focus on the container, otherwise on the add button
      // (ideally, we'd put it on the empty list add button instead, but that's a bit complicated with
      // it being hidden. The container isn't perfect, but good enough.)
      workflow?.steps.length === 1
        ? stepListContainerRef.current?.focus()
        : stepsListAddButtonRef.current?.focus();
    },
    [closePreviewStepPane, currentPreviewStep?.id, workflow?.steps.length],
  );

  if (isLoading) {
    return (
      <Page>
        <EditWorkflowHeader wizardStep={WizardStep.STEPS} />
        <Loading />
      </Page>
    );
  }

  if (!workflow) {
    throw new Error("Couldn't get information about this Workflow.");
  }

  return (
    <Page>
      <EditWorkflowHeader wizardStep={WizardStep.STEPS} />
      <ContentWithBottomDrawer>
        <ContentWithBottomDrawer.Main mainRef={stepListContainerRef}>
          <StyledWorkflowIncompleteDataMessages workflow={workflow} />
          {workflow.steps.length ? (
            <StepList
              addButtonRef={stepsListAddButtonRef}
              onDeleteSuccess={onDeleteSuccess}
              openModalForNewStep={openModalForNewStep}
              openModalForExistingStep={openModalForExistingStep}
              openPreviewStepPane={openPreviewStepPane}
              workflow={workflow}
              highlightedStepId={currentPreviewStep?.id}
            />
          ) : (
            <EmptyStepList
              onAddNew={openModalForNewStep}
              organizationId={organizationId}
            />
          )}
        </ContentWithBottomDrawer.Main>
        <WorkflowStepPreviewBottomDrawer
          previewStep={currentPreviewStep}
          organizationId={organizationId}
          onClose={closePreviewStepPane}
        />
      </ContentWithBottomDrawer>
      {!!currentlyOpenStepType && (
        <EditStepModal
          isOpen={!!currentlyOpenStepType}
          onClose={closeStepModal}
          onSubmitStep={submitStep}
          organizationId={organizationId}
          stepType={currentlyOpenStepType}
          stepId={currentlyOpenStepId}
          workflow={workflow}
        />
      )}
    </Page>
  );
}
